import { useEffect } from 'react';
import { TextSection } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { DestinationType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import DOMPurify from 'dompurify';
import { TemplatorV2Queries } from '@frontend/api-templator-v2';
import { useDynamicFields } from '@frontend/dynamic-fields-hooks';
import { useAppScopeStore } from '@frontend/scope';
import { dynamicFieldLabelDataAttribute } from '@frontend/design-system';
import { useComposerSection } from '../../hooks';
import { ComposerSectionOutputProps } from '../../types';
import { useContentComposer } from '../content-composer-provider';
import { SectionFrame } from '../sections/image-elements';

export const TextOutput = ({ onRenderReady, renderMode }: ComposerSectionOutputProps) => {
  const { props } = useComposerSection<TextSection>();
  const { composerMode, templateTypeSlug } = useContentComposer();
  const isFeedbackMode = composerMode === 'feedback';

  const { selectedLocationIds: locationIds = [], accessibleLocationData } = useAppScopeStore();
  const locationId = locationIds[0];
  const locationData = accessibleLocationData[locationId!];
  const { bindingsList } = useDynamicFields([templateTypeSlug]);

  const sanitizedText = DOMPurify.sanitize(props.text ?? '', {
    // Remove the label data attribute from all dynamic fields.
    // This "fixes" an edge case where if the user copies and pastes preview HTML back into the editor,
    // the dynamic field styling is removed and appears as static text.
    FORBID_ATTR: [dynamicFieldLabelDataAttribute],
  });
  const { data: transformedText = '' } = TemplatorV2Queries.useRender(
    {
      templateTypeSlug,
      templateString: sanitizedText,
      timezone: locationData?.timezone ?? '',
      bindingsList,
      destinationType: isFeedbackMode ? DestinationType_Enum.SMS : DestinationType_Enum.EMAIL,
      locale: 'en_US', // TODO: Dynamically get this from somewhere
    },
    {
      select: (data) => data.message.message,
      enabled: renderMode === 'preview',
      onSettled: onRenderReady,
    }
  );

  useEffect(() => {
    if (renderMode !== 'preview' || !!transformedText) {
      onRenderReady?.();
    }
  }, [transformedText]);

  return (
    <SectionFrame
      {...props.frame}
      alignment='left'
      style={{ lineHeight: isFeedbackMode ? '1.5' : undefined }}
      dangerouslySetInnerHTML={{ __html: renderMode === 'preview' ? transformedText : sanitizedText }}
    />
  );
};
